import React, { useEffect, useState, useRef } from "react";
import { Grid, Typography } from "@mui/material";
import { AppBtn } from "../components/AppBtn";
import { Footer } from "../components/Footer";
import { useAppSelector } from "../redux/hooks";
import { useNavigate } from "react-router-dom";
import useLogin from "../hooks/useLogin";

declare global {
  interface Window {
    OneSignalDeferred: Array<(OneSignal: any) => Promise<void>>;
    OneSignal: any;
  }
}

const ONESIGNAL_APP_ID = "20bee3f3-ce2e-4855-be9c-376565090a84";

export const Apps = () => {
  const { registerDeviceOnce } = useLogin();
  const [oneSignalInitialized, setOneSignalInitialized] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initOneSignal = async () => {
      try {
        const OneSignal = window.OneSignal;

        if (OneSignal?._initAlreadyCalled || OneSignal?._initialized) {
          console.log("OneSignal ya inicializado, reutilizando");
          setOneSignalInitialized(true);
          setIsSubscribed(OneSignal.User?.PushSubscription?.optedIn ?? false);
          return;
        }

        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OS: any) => {
          try {
            await OS.init({
              appId: ONESIGNAL_APP_ID,
              serviceWorkerParam: { scope: "/" },
              serviceWorkerPath: "OneSignalSDKWorker.js",
              allowLocalhostAsSecureOrigin: true,
            });
          } catch (e: any) {
            if (e?.message?.includes("already initialized")) {
              console.log("OneSignal ya estaba inicializado");
            } else {
              throw e;
            }
          }

          console.log("OneSignal v16 listo");
          setOneSignalInitialized(true);

          OS.User.PushSubscription.addEventListener("change", (e: any) => {
            console.log("Suscripción cambió:", e.current);
            setIsSubscribed(!!e.current.optedIn);
          });

          setIsSubscribed(OS.User.PushSubscription.optedIn ?? false);

          if (!OS.Notifications.permission) {
            try {
              await OS.Slidedown.promptPush();
            } catch (err) {
              console.log("Prompt ya mostrado o cerrado:", err);
            }
          }
        });
      } catch (error) {
        console.error("Error inicializando OneSignal:", error);
      }
    };

    initOneSignal();
  }, []);

  const navigate = useNavigate();
  const { nombre, acceso_Pantallas, usuario, empresa, casa, devices } =
    useAppSelector(state => state.auth.userData!);
  const isDevEnvironment =
    window.location.origin === "http://localhost:3000" ;
  const isControlEnvironment =
    window.location.hostname === "control.ls-sys.com";

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!oneSignalInitialized || !isSubscribed) return;
    const OS = window.OneSignal;
    if (!OS) return;

    const configureUser = async () => {
      try {
        const deviceId = OS.User.PushSubscription.id;
        if (!deviceId) return;

        const hasFullUser = usuario && empresa != null && casa != null;
        const externalId = hasFullUser
          ? `${usuario}-${empresa}-${casa}-${devices}`
          : deviceId;

        if (hasFullUser) {
          try {
            await OS.login(externalId);
          } catch (loginErr) {
            console.warn("OneSignal login conflict (ya existe), continuando:", loginErr);
          }
          OS.User.addTags({
            uid: usuario,
            emp: String(empresa),
            cas: String(casa),
          });
          console.log("OneSignal tags OK:", externalId);
        }

        await registerDeviceOnce(usuario ?? "anon", deviceId, externalId);
      } catch (error) {
        console.error("Error configurando OneSignal:", error);
      }
    };

    configureUser();
  }, [oneSignalInitialized, isSubscribed, usuario, empresa, casa, devices, registerDeviceOnce]);

  const handleKeyDown = (e: any) => {
    if (
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      (e.keyCode >= 65 && e.keyCode <= 90) ||
      (e.keyCode >= 97 && e.keyCode <= 122)
    ) {
      if (
        acceso_Pantallas.some(
          i =>
            i.pantalla_Acceso === "ingreso-garita" ||
            i.pantalla_Acceso === "all"
        )
      )
        navigate("ingreso-garita");
    }
  };

  const apps = [
    {
      name: "Crear Accesos",
      icon: "/assets/create-access.png",
      url: "crear-acceso",
    },
    {
      name: "Crear Accesos Frecuente",
      icon: "/assets/access-time.png",
      url: "crear-acceso-frecuente",
    },
    {
      name: "Anular Acceso",
      icon: "/assets/deny-access.png",
      url: "anular-acceso",
    },
    {
      name: "Ingreso Garita",
      icon: "/assets/ingreso.png",
      url: "ingreso-garita",
    },
    {
      name: "Egreso Garita",
      icon: "/assets/egreso.png",
      url: "egreso-garita",
    },
    {
      name: "Perfil de Usuario",
      icon: "/assets/user.png",
      url: "perfil-usuario",
    },
    {
      name: "Mis Accesos",
      icon: "/assets/history.png",
      url: "historial-accesos",
    },
    {
      name: "Estado de Cuenta",
      icon: "/assets/estado-cuenta.png",
      url: "estado-cuenta",
    },
    {
      name: "Pagos Recibidos",
      icon: "/assets/pagos-recibidos.png",
      url: "pagos-recibidos",
    },
    {
      name: "FOTOS",
      icon: "https://cdn-icons-png.flaticon.com/512/38/38967.png",
      url: "fotos-pruebas",
    },
    {
      name: "Lectura de Agua",
      icon: "/assets/water-drop.png",
      url: "lectura-agua",
    },
    {
      name: "Consumo de Agua",
      icon: "/assets/water-meter.png",
      url: "historico-agua",
    },
    {
      name: "Reservas",
      icon: "/assets/calendar.png",
      url: "reservas",
    },
    {
      name: "Administrador de Reservas",
      icon: "/assets/calendar-admin.png",
      url: "reservas-admin",
    },
    {
      name: "Control de Inmuebles",
      icon: "/assets/home-manager.png",
      url: "control-inmuebles",
    },
    {
      name: "Pagos Inmuebles",
      icon: "/assets/pagos-recibidos.png",
      url: "pagos-inmuebles",
    },
    {
      name: "Encuestas",
      icon: "/assets/encuesta.png",
      url: "encuestas",
    },
    {
      name: "Incidentes",
      icon: "/assets/exclamacion.png",
      url: "crear-incidente",
    },
    {
      name: "Mis Reservas",
      icon: "/assets/calendario_.png",
      url: "historial-reserva",
    },
    {
      name: "Cargos y abonos",
      icon: "/assets/analysis.png",
      url: "cargos-abonos",
    },
    {
      name: "Sponsor",
      icon: "/assets/analysis.png",
      url: "sponsor",
    },
    {
      name: "Abonos por tipo",
      icon: "/assets/pie-chat.png",
      url: "ingresos-tipo",
    },
    {
      name: "Historial de Incidentes",
      icon: "/assets/clock.png",
      url: "historial-incidentes",
    },
    {
      name: "Mis Accesos Frecuentes",
      icon: "/assets/ids.png",
      url: "accesos-frecuentes",
    },
  ];
  const devURLs = ["fotos-pruebas", "encuestas"];

  const controlURLs = ["cargos-abonos", "ingresos-tipo"];

  return (
    <>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        textAlign="center"
        marginTop={5}
      >
        {nombre}
      </Typography>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        minHeight={"50vh"}
        paddingBottom={20}
        sx={{
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {
          // Mostrar menú específico si la ruta es control.ls-sys.com
          isControlEnvironment ? (
            <>
              {isControlEnvironment &&
                apps.map(
                  app =>
                    controlURLs.includes(app.url) && (
                      <Grid
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        item
                        xs={6}
                        sm={4}
                        md={4}
                        lg={2}
                        xl={2}
                        key={app.name}
                      >
                        <AppBtn name={app.name} icon={app.icon} url={app.url} />
                      </Grid>
                    )
                )}
            </>
          ) : (
            <>
              {apps.map(
                app =>
                  acceso_Pantallas.some(
                    pantalla =>
                      pantalla.pantalla_Acceso === app.url ||
                      pantalla.pantalla_Acceso === "all"
                  ) &&
                  !devURLs.includes(app.url) && (
                    <Grid
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      item
                      xs={6}
                      sm={4}
                      md={4}
                      lg={2}
                      xl={2}
                      key={app.name}
                    >
                      <AppBtn name={app.name} icon={app.icon} url={app.url} />
                    </Grid>
                  )
              )}
              {/* Display development apps: */}
              {isDevEnvironment &&
                apps.map(
                  app =>
                    devURLs.includes(app.url) && (
                      <Grid
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        item
                        xs={6}
                        sm={4}
                        md={4}
                        lg={2}
                        xl={2}
                        key={app.name}
                      >
                        <AppBtn name={app.name} icon={app.icon} url={app.url} />
                      </Grid>
                    )
                )}
              <Grid
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                item
                xs={6}
                sm={4}
                md={4}
                lg={2}
                xl={2}
                key={"notificaciones"}
              >
                <AppBtn
                  name={"Publicaciones"}
                  icon={"assets/megafono.png"}
                  url={"publicaciones"}
                />
              </Grid>
            </>
          )
        }
      </Grid>
      <Footer />
    </>
  );
};
